import Search from "@icons/search.svg?url";
import { t } from "@lingui/core/macro";
import { useSuspenseQuery } from "@tanstack/react-query";
import React from "react";
import { useFormContext } from "react-hook-form";

import { InputField } from "@/common/components/form/input-field/input-field";
import { invariant } from "@/common/utils/invariant";
import { usePostalCodesQuery } from "@/routes/shared/api/get-postal-codes";

import { Fields } from "./storage-form";

const PostalCodeField = ({ label }: { label: React.ReactNode }) => {
  const firstRun = React.useRef(true);
  const form = useFormContext<Fields>();
  const postalCodes = useSuspenseQuery(usePostalCodesQuery());
  const postalCodeField = form.register("postalCode");
  const postalCodeValue = form.watch("postalCode");
  const { setSuggestionsVisible, suggestions } = usePostalCodeField();

  // just make sure we have the postal code label
  React.useEffect(() => {
    if (!firstRun.current) {
      return;
    }
    firstRun.current = false;
    const postalCode = postalCodes.data.find(
      (pc) => pc.code === postalCodeValue,
    );
    if (postalCodeValue.split(" ").length === 1 && postalCode) {
      form.setValue("postalCode", postalCode.label);
    }
  }, [form, postalCodeValue, postalCodes.data]);

  return (
    <InputField
      autoComplete="off"
      label={label}
      name="postalCode"
      onChange={async (e) => {
        await postalCodeField.onChange(e);
        setSuggestionsVisible(true);
      }}
      onFocus={() => {
        if (suggestions.length > 0) {
          setSuggestionsVisible(true);
        }
      }}
      options={{
        required: t`Vyplňte prosím PSČ`,
        validate: (value: string) =>
          postalCodes.data.some((pc) =>
            pc.label.toLowerCase().includes(value.toLowerCase()),
          ) || t`Vyberte PSČ ze seznamu`,
      }}
      placeholder={t`Vyhledat`}
      style={{
        // eslint-disable-next-line lingui/no-unlocalized-strings
        background: `url(${Search}) right 16px center no-repeat`,
      }}
    />
  );
};

const Suggestions = ({ className }: { className?: string }) => {
  const { selectSuggestion, suggestionsVisible, suggestions } =
    usePostalCodeField();
  return suggestionsVisible && suggestions.length > 0 ? (
    <ul
      className={`shadow-can-light-box absolute flex max-h-[140px] flex-col overflow-y-auto rounded-lg bg-white ${className}`}
    >
      {suggestions.map((match) => (
        <li
          className="border-b-can-silver-cloud flex border-b"
          key={match.label}
        >
          <button
            className="grow px-4 py-2 text-left"
            onClick={() => selectSuggestion(match.label)}
          >
            {match.label}
          </button>
        </li>
      ))}
    </ul>
  ) : null;
};

type TContext = {
  setSuggestionsVisible: (v: boolean) => void;
  suggestionsVisible: boolean;
  suggestions: Array<{
    code: string;
    name: string;
    label: string;
    latitude: number;
    longitude: number;
  }>;
  selectSuggestion: (suggestion: string) => void;
};

const Context = React.createContext<TContext | undefined>(undefined);

const usePostalCodeField = () => {
  const context = React.useContext(Context);

  invariant(
    context,
    // eslint-disable-next-line lingui/no-unlocalized-strings
    "Context only available within <PostalCodeFieldContext />",
  );

  return context;
};

const Provider = ({ children }: React.PropsWithChildren) => {
  const form = useFormContext<Fields>();
  const postalCodes = useSuspenseQuery(usePostalCodesQuery());
  const postalCodeValue = form.watch("postalCode");
  const [suggestionsVisible, setSuggestionsVisible] = React.useState(false);
  const suggestions =
    postalCodeValue.length > 0
      ? postalCodes.data.filter((c) =>
          c.label.toLowerCase().includes(postalCodeValue.toLowerCase()),
        )
      : [];

  const selectSuggestion = (code: string) => {
    form.setValue("postalCode", code);
    setSuggestionsVisible(false);
  };

  return (
    <Context.Provider
      value={{
        selectSuggestion,
        setSuggestionsVisible,
        suggestions,
        suggestionsVisible,
      }}
    >
      {children}
    </Context.Provider>
  );
};

PostalCodeField.Suggestions = Suggestions;
PostalCodeField.Provider = Provider;

export { PostalCodeField };
